import { useEffect, useState } from "react";
import { api, idr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import InputRupiah from "../components/InputRupiah";
import {
  Wrench, Plus, X, Search, MessageCircle, ShieldCheck, Banknote,
  History, Trash2, StickyNote, RefreshCw, Camera, UserCog,
} from "lucide-react";
import LoadingState from "@/components/ui/loading-state";
import { useDebounce } from "@/hooks/useDebounce";
import { useModalGuard, UnsavedGuardBar } from "@/hooks/useModalGuard";

/* Papan status TANPA drag-drop — keputusan sumber (Sixcodex): perubahan status
   harus lewat drawer + tombol transisi supaya setiap perubahan tercatat siapa &
   kenapa di riwayat. Drag-drop enak dipakai tapi menghasilkan perubahan tanpa
   audit (dan salah-geser diam-diam). */
const BOARD = ["RECEIVED", "DIAGNOSIS", "IN_PROGRESS", "WAITING_PARTS", "COMPLETED", "PICKED_UP", "CANCELLED"];

const STATUS_META = {
  RECEIVED: { label: "Masuk", chip: "bg-amber-100 text-amber-800", bar: "bg-amber-400" },
  DIAGNOSIS: { label: "Diagnosa", chip: "bg-sky-100 text-sky-800", bar: "bg-sky-400" },
  IN_PROGRESS: { label: "Proses", chip: "bg-indigo-100 text-indigo-800", bar: "bg-indigo-400" },
  WAITING_PARTS: { label: "Menunggu Sparepart", chip: "bg-orange-100 text-orange-800", bar: "bg-orange-400" },
  COMPLETED: { label: "Selesai", chip: "bg-emerald-100 text-emerald-800", bar: "bg-emerald-500" },
  PICKED_UP: { label: "Diambil", chip: "bg-slate-100 text-slate-600", bar: "bg-slate-400" },
  CANCELLED: { label: "Dibatalkan", chip: "bg-rose-100 text-rose-700", bar: "bg-rose-400" },
};

const PRIORITAS_META = {
  Urgent: "bg-rose-100 text-rose-700",
  Normal: "bg-slate-100 text-slate-600",
  Low: "bg-sky-100 text-sky-700",
};

const NOTE_TYPES = ["DIAGNOSIS", "REPAIR", "UPDATE", "CUSTOMER"];

const emptyIntake = () => ({
  pelanggan_nama: "", no_hp: "", device_model: "", storage: "", warna: "",
  imei: "", keluhan: "", teknisi_id: "", prioritas: "Normal",
  estimasi_biaya: 0, service_fee: 0, warranty_days: 30, cabang: "",
  catatan_awal: "", photos: [],
});

/* Foto serah-terima dikompres di browser (maks 900px, JPEG 72%) sebelum jadi
   base64 — backend menolak foto >±300KB karena disimpan di dokumen order. */
const kompresFoto = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const maxW = 900;
      const scale = Math.min(1, maxW / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.72));
    };
    img.onerror = reject;
    img.src = reader.result;
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const tglPendek = (iso) => (iso ? String(iso).slice(0, 10) : "-");
const jamPendek = (iso) => (iso ? `${String(iso).slice(0, 10)} ${String(iso).slice(11, 16)}` : "-");

export default function Servis() {
  const { can } = useAuth();
  const bolehBuat = can("servis", "create");
  const bolehUbah = can("servis", "update");

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const [teknisiList, setTeknisiList] = useState([]);
  const [akunList, setAkunList] = useState([]);

  const [intake, setIntake] = useState(null);        // form terima unit (modal)
  const [detail, setDetail] = useState(null);        // isi drawer
  const [detailLoading, setDetailLoading] = useState(false);

  // Form-form kecil di drawer
  const [statusNote, setStatusNote] = useState("");
  const [partForm, setPartForm] = useState({ nama: "", qty: 1, harga_modal: 0, harga_jual: 0 });
  const [noteForm, setNoteForm] = useState({ tipe: "UPDATE", isi: "", is_customer_visible: false });
  const [bayarForm, setBayarForm] = useState({ nominal: "", sumber_akun_id: "" });
  const [biayaForm, setBiayaForm] = useState(null);  // {estimasi_biaya, service_fee, warranty_days}

  const load = async (cari = q) => {
    setLoading(true);
    try {
      const params = {};
      if (cari.trim()) params.q = cari.trim();
      const [a, b] = await Promise.all([
        api.get("/servis", { params }),
        api.get("/servis/summary"),
      ]);
      setItems(a.data.items || []);
      setSummary(b.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data servis");
    } finally { setLoading(false); }
  };

  const qDebounced = useDebounce(q, 400);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(qDebounced); }, [qDebounced]);

  useEffect(() => {
    // Dropdown teknisi dari data karyawan; kolom ringkas saja yang terkirim
    // untuk user tanpa izin employees.view (kebijakan endpoint /employees).
    api.get("/employees")
      .then((r) => setTeknisiList((r.data || []).filter((e) => e.is_active !== false)))
      .catch(() => {});
    // Akun kas/bank untuk form pembayaran — kalau gagal (mis. teknisi tanpa
    // akses finance), form bayar tampil nonaktif dengan penjelasan.
    api.get("/finance/accounts/kas-bank")
      .then((r) => setAkunList(r.data || []))
      .catch(() => {});
  }, []);

  const openDetail = async (sid) => {
    setDetailLoading(true);
    setDetail({ orderId: sid });
    try {
      const r = await api.get(`/servis/${sid}`);
      setDetail({ orderId: sid, ...r.data });
      setBiayaForm({
        estimasi_biaya: r.data.order.estimasi_biaya || 0,
        service_fee: r.data.order.service_fee || 0,
        warranty_days: r.data.order.warranty_days ?? 30,
      });
      setStatusNote("");
      setPartForm({ nama: "", qty: 1, harga_modal: 0, harga_jual: 0 });
      setNoteForm({ tipe: "UPDATE", isi: "", is_customer_visible: false });
      setBayarForm({ nominal: "", sumber_akun_id: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuka detail order");
      setDetail(null);
    } finally { setDetailLoading(false); }
  };

  const reloadDetail = async () => {
    if (detail?.orderId) await openDetail(detail.orderId);
    await load();
  };

  const simpanIntake = async () => {
    setBusy(true);
    try {
      const r = await api.post("/servis", {
        ...intake,
        teknisi_id: intake.teknisi_id || null,
        estimasi_biaya: Number(intake.estimasi_biaya) || 0,
        service_fee: Number(intake.service_fee) || 0,
        warranty_days: Number(intake.warranty_days) || 0,
      });
      toast.success(r.data.message || "Tiket dibuat");
      if (r.data.wa_terkirim) toast.info("Tanda terima otomatis terkirim via WhatsApp");
      setIntake(null);
      await load();
      if (r.data.order?.id) await openDetail(r.data.order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan order");
    } finally { setBusy(false); }
  };

  // Modal intake ada di JSX yang sama dengan komponen halaman (tidak pernah
  // unmount saat ditutup) — `active` dipakai supaya dirty-state di-reset
  // setiap kali modal dibuka lagi, bukan cuma sekali saat mount pertama.
  const intakeGuard = useModalGuard({
    active: !!intake,
    dangerous: true,
    busy,
    disabled: !intake?.pelanggan_nama?.trim() || !intake?.device_model?.trim() || !intake?.keluhan?.trim(),
    onClose: () => setIntake(null),
    onSubmit: simpanIntake,
  });

  const tambahFoto = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 4 - (intake.photos?.length || 0));
    for (const f of files) {
      try {
        const dataUrl = await kompresFoto(f);
        setIntake((s) => ({ ...s, photos: [...(s.photos || []), dataUrl] }));
      } catch { toast.error(`Gagal membaca foto ${f.name}`); }
    }
  };

  const ubahStatus = async (target) => {
    let catatan = statusNote.trim();
    if (target === "CANCELLED" && !catatan) {
      catatan = (window.prompt("Alasan pembatalan (wajib):") || "").trim();
      if (!catatan) return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/servis/${detail.orderId}/status`, { status: target, catatan });
      toast.success(r.data.message || "Status diubah");
      if (r.data.wa_terkirim) toast.info("Notifikasi otomatis terkirim via WhatsApp");
      else if (r.data.wa_link) toast.info("Gunakan tombol WhatsApp untuk kabari pelanggan");
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah status");
    } finally { setBusy(false); }
  };

  const simpanBiaya = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/servis/${detail.orderId}`, {
        estimasi_biaya: Number(biayaForm.estimasi_biaya) || 0,
        service_fee: Number(biayaForm.service_fee) || 0,
        warranty_days: Number(biayaForm.warranty_days) || 0,
      });
      toast.success(r.data.message || "Biaya diperbarui");
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan biaya");
    } finally { setBusy(false); }
  };

  const gantiTeknisi = async (teknisi_id) => {
    setBusy(true);
    try {
      await api.patch(`/servis/${detail.orderId}`, { teknisi_id });
      toast.success(teknisi_id ? "Teknisi di-assign" : "Assignment dilepas");
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah teknisi");
    } finally { setBusy(false); }
  };

  const tambahPart = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/servis/${detail.orderId}/parts`, {
        ...partForm,
        qty: Number(partForm.qty) || 1,
        harga_modal: Number(partForm.harga_modal) || 0,
        harga_jual: Number(partForm.harga_jual) || 0,
      });
      toast.success(r.data.message || "Sparepart ditambahkan");
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah sparepart");
    } finally { setBusy(false); }
  };

  const hapusPart = async (pid) => {
    setBusy(true);
    try {
      const r = await api.delete(`/servis/${detail.orderId}/parts/${pid}`);
      toast.success(r.data.message || "Sparepart dihapus");
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus sparepart");
    } finally { setBusy(false); }
  };

  const tambahCatatan = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/servis/${detail.orderId}/catatan`, noteForm);
      toast.success(r.data.message || "Catatan tersimpan");
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan catatan");
    } finally { setBusy(false); }
  };

  const bayar = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/servis/${detail.orderId}/bayar`, {
        nominal: Number(bayarForm.nominal) || 0,
        sumber_akun_id: bayarForm.sumber_akun_id,
      });
      toast.success(r.data.message || "Pembayaran tercatat");
      await reloadDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mencatat pembayaran");
    } finally { setBusy(false); }
  };

  const klaimGaransi = async () => {
    const alasan = (window.prompt("Alasan klaim garansi (keluhan pelanggan):") || "").trim();
    if (!alasan) return;
    setBusy(true);
    try {
      const r = await api.post(`/servis/${detail.orderId}/klaim-garansi`, { alasan });
      toast.success(r.data.message || "Klaim garansi dibuka");
      await load();
      if (r.data.order?.id) await openDetail(r.data.order.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuka klaim garansi");
    } finally { setBusy(false); }
  };

  const order = detail?.order;
  const editableData = order && bolehUbah && !["PICKED_UP", "CANCELLED"].includes(order.status);
  const editableParts = order && bolehUbah && !["COMPLETED", "PICKED_UP", "CANCELLED"].includes(order.status);
  const sisaTagihan = order ? Math.max(0, (order.total_akhir || 0) - (order.total_dibayar || 0)) : 0;
  const garansiAktif = order && ["COMPLETED", "PICKED_UP"].includes(order.status)
    && order.warranty_expires_at && new Date(order.warranty_expires_at) > new Date();

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="servis-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-col lg:flex-row gap-3">
        <div>
          <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">POS &amp; Sales</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            <Wrench className="w-7 h-7 text-emerald-600" /> Service Center
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Tiket servis dari intake sampai diambil. Ubah status lewat drawer —
            setiap perubahan tercatat di riwayat, dan unit
            <span className="font-medium text-slate-800"> tidak bisa diserahkan sebelum lunas</span>.
          </p>
        </div>
        {bolehBuat && (
          <button onClick={() => setIntake(emptyIntake())} data-testid="srv-add"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Terima Unit
          </button>
        )}
      </div>

      {/* Kartu ringkasan per status */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {BOARD.map((s) => (
          <div key={s} className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
            <div className={`h-1 rounded-full ${STATUS_META[s].bar} mb-2`} />
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{STATUS_META[s].label}</p>
            <p className="font-display text-2xl font-bold mt-1 text-slate-900" data-testid={`srv-count-${s}`}>
              {summary[s] ?? 0}
            </p>
          </div>
        ))}
      </div>

      {/* Pencarian */}
      <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); load(q); }}>
        <div className="relative flex-1 md:max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="srv-search"
                 placeholder="Cari tiket / nama / IMEI / no HP..."
                 className="h-9 w-full pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
        </div>
        <button type="submit" data-testid="srv-search-btn"
                className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700">
          Cari
        </button>
        <button type="button" onClick={() => { setQ(""); load(""); }} data-testid="srv-refresh"
                className="h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-500">
          <RefreshCw className="w-4 h-4" />
        </button>
      </form>

      {/* Papan status */}
      {loading ? (
        <LoadingState loading data-testid="srv-board-loading" />
      ) : (
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-3 min-w-max">
            {BOARD.map((s) => {
              const kolom = items.filter((it) => it.status === s);
              return (
                <div key={s} className="w-[270px] shrink-0 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="px-3 py-2.5 flex items-center justify-between border-b border-slate-200">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_META[s].chip}`}>
                      {STATUS_META[s].label}
                    </span>
                    <span className="text-xs text-slate-400 font-medium">{kolom.length}</span>
                  </div>
                  <div className="p-2 space-y-2 max-h-[60vh] overflow-y-auto">
                    {kolom.length === 0 && (
                      <p className="text-center text-xs text-slate-400 py-4">Kosong</p>
                    )}
                    {kolom.map((it) => (
                      <button key={it.id} onClick={() => openDetail(it.id)}
                              data-testid={`srv-card-${it.id}`}
                              className="w-full text-left bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:border-emerald-400 hover:shadow transition">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-[11px] text-slate-500">{it.nomor_tiket}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${PRIORITAS_META[it.prioritas] || PRIORITAS_META.Normal}`}>
                            {it.prioritas}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-slate-900 mt-1 truncate">{it.pelanggan_nama}</p>
                        <p className="text-xs text-slate-600 truncate">
                          {it.device_model}{it.storage ? ` ${it.storage}` : ""}{it.warna ? ` · ${it.warna}` : ""}
                        </p>
                        <p className="text-xs text-slate-400 truncate mt-0.5">{it.keluhan}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-xs font-medium text-slate-700">{idr(it.total_akhir)}</span>
                          {it.source_type === "WARRANTY_CLAIM" && (
                            <span className="text-[10px] inline-flex items-center gap-0.5 text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">
                              <ShieldCheck className="w-3 h-3" /> Garansi
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">
                          {it.teknisi_nama ? `Teknisi: ${it.teknisi_nama}` : "Belum ada teknisi"} · {tglPendek(it.created_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ Drawer detail ============ */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={() => setDetail(null)}>
          <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl"
               onClick={(e) => e.stopPropagation()} data-testid="srv-drawer">
            {detailLoading || !order ? (
              <div className="p-5"><LoadingState loading jumlahKartu={1} data-testid="srv-detail-loading" /></div>
            ) : (
              <>
                <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-semibold text-slate-900">{order.nomor_tiket}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_META[order.status]?.chip}`}>
                      {STATUS_META[order.status]?.label || order.status}
                    </span>
                    {order.source_type === "WARRANTY_CLAIM" && (
                      <span className="text-[10px] inline-flex items-center gap-0.5 text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">
                        <ShieldCheck className="w-3 h-3" /> Klaim dari {order.klaim_dari_tiket}
                      </span>
                    )}
                  </div>
                  <button onClick={() => setDetail(null)} data-testid="srv-drawer-close"
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-5 space-y-5">
                  {/* Info pelanggan & unit */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Pelanggan</p>
                      <p className="font-semibold text-slate-900 mt-0.5">{order.pelanggan_nama}</p>
                      <p className="text-slate-500 text-xs">{order.pelanggan_hp || "tanpa no HP"}</p>
                      {order.cabang && <p className="text-slate-500 text-xs">Cabang: {order.cabang}</p>}
                    </div>
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Unit</p>
                      <p className="font-semibold text-slate-900 mt-0.5">
                        {order.device_model}{order.storage ? ` ${order.storage}` : ""}{order.warna ? ` · ${order.warna}` : ""}
                      </p>
                      {order.imei && <p className="text-slate-500 text-xs font-mono">IMEI {order.imei}</p>}
                      <p className="text-slate-500 text-xs">Masuk {jamPendek(order.created_at)}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Keluhan</p>
                    <p className="text-sm text-slate-700 mt-0.5">{order.keluhan}</p>
                  </div>

                  {/* Foto serah terima */}
                  {(order.photos || []).length > 0 && (
                    <div>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5 flex items-center gap-1">
                        <Camera className="w-3.5 h-3.5" /> Foto Serah Terima
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {order.photos.map((p, i) => (
                          <img key={i} src={p} alt={`Foto unit ${i + 1}`}
                               className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Teknisi */}
                  <div>
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-1 flex items-center gap-1">
                      <UserCog className="w-3.5 h-3.5" /> Teknisi
                    </p>
                    {editableData ? (
                      <select value={order.teknisi_id || ""} disabled={busy} data-testid="srv-teknisi"
                              onChange={(e) => gantiTeknisi(e.target.value || "")}
                              className="h-9 w-full px-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                        <option value="">— Belum di-assign —</option>
                        {teknisiList.map((t) => <option key={t.id} value={t.id}>{t.nama}</option>)}
                      </select>
                    ) : (
                      <p className="text-sm text-slate-700">{order.teknisi_nama || "Belum di-assign"}</p>
                    )}
                  </div>

                  {/* Ubah status */}
                  {bolehUbah && (detail.transisi || []).length > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold mb-2">Ubah Status</p>
                      <input value={statusNote} onChange={(e) => setStatusNote(e.target.value)}
                             data-testid="srv-status-note" placeholder="Catatan perubahan (opsional; wajib saat batal)"
                             className="h-9 w-full px-3 rounded-lg border border-slate-200 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                      <div className="flex gap-2 flex-wrap">
                        {detail.transisi.map((t) => (
                          <button key={t} onClick={() => ubahStatus(t)} disabled={busy}
                                  data-testid={`srv-status-${t}`}
                                  className={`h-9 px-3 rounded-lg text-sm font-medium disabled:opacity-50 ${
                                    t === "CANCELLED"
                                      ? "border border-rose-300 text-rose-700 hover:bg-rose-50"
                                      : t === "COMPLETED" || t === "PICKED_UP"
                                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                                        : "border border-slate-300 text-slate-700 hover:bg-white"
                                  }`}>
                            → {STATUS_META[t]?.label || t}
                          </button>
                        ))}
                      </div>
                      {order.status === "COMPLETED" && sisaTagihan > 0.5 && (
                        <p className="text-xs text-rose-600 mt-2">
                          Sisa tagihan {idr(sisaTagihan)} — unit tidak bisa ditandai Diambil sebelum lunas.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Tombol WA sesuai status saat ini */}
                  {detail.wa_link && (
                    <a href={detail.wa_link} target="_blank" rel="noreferrer" data-testid="srv-wa"
                       className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                      <MessageCircle className="w-4 h-4" /> WhatsApp pelanggan ({STATUS_META[order.status]?.label})
                    </a>
                  )}
                  {order.status === "WAITING_PARTS" && (
                    <p className="text-xs text-slate-500">
                      Status Menunggu Sparepart sengaja tidak dikirim ke pelanggan — urusan internal.
                    </p>
                  )}

                  {/* Biaya & sparepart */}
                  <div className="border border-slate-200 rounded-xl p-3 space-y-3">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold">Biaya &amp; Sparepart</p>
                    {editableData && biayaForm ? (
                      <div className="grid grid-cols-3 gap-2 items-end">
                        <div>
                          <label className="text-xs text-slate-600">Estimasi</label>
                          <InputRupiah value={biayaForm.estimasi_biaya} testid="srv-f-estimasi"
                                 onChange={(n) => setBiayaForm((f) => ({ ...f, estimasi_biaya: n }))}
                                 className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600">Biaya Jasa</label>
                          <InputRupiah value={biayaForm.service_fee} testid="srv-f-jasa"
                                 onChange={(n) => setBiayaForm((f) => ({ ...f, service_fee: n }))}
                                 className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                        </div>
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <label className="text-xs text-slate-600">Garansi (hari)</label>
                            <input type="number" value={biayaForm.warranty_days} data-testid="srv-f-garansi"
                                   onChange={(e) => setBiayaForm((f) => ({ ...f, warranty_days: e.target.value }))}
                                   className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                          </div>
                          <button onClick={simpanBiaya} disabled={busy} data-testid="srv-biaya-save"
                                  className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm disabled:opacity-50">
                            Simpan
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500">
                        Estimasi {idr(order.estimasi_biaya)} · Jasa {idr(order.service_fee)} · Garansi {order.warranty_days ?? 0} hari
                      </p>
                    )}

                    {(order.parts || []).length > 0 && (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-[11px] text-slate-500 uppercase">
                            <th className="text-left font-semibold py-1">Part</th>
                            <th className="text-right font-semibold">Qty</th>
                            <th className="text-right font-semibold">Modal</th>
                            <th className="text-right font-semibold">Jual</th>
                            {editableParts && <th />}
                          </tr>
                        </thead>
                        <tbody>
                          {order.parts.map((p) => (
                            <tr key={p.id} className="border-t border-slate-100">
                              <td className="py-1.5 text-slate-800">{p.nama}</td>
                              <td className="text-right text-slate-600">{p.qty}</td>
                              <td className="text-right text-slate-500">{idr(p.harga_modal)}</td>
                              <td className="text-right text-slate-800">{idr(p.harga_jual)}</td>
                              {editableParts && (
                                <td className="text-right">
                                  <button onClick={() => hapusPart(p.id)} disabled={busy}
                                          data-testid={`srv-part-del-${p.id}`}
                                          className="p-1 rounded hover:bg-rose-50 text-rose-500 disabled:opacity-50">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {editableParts && (
                      <div className="grid grid-cols-5 gap-2 items-end">
                        <div className="col-span-2">
                          <label className="text-xs text-slate-600">Nama part</label>
                          <input value={partForm.nama} data-testid="srv-part-nama"
                                 onChange={(e) => setPartForm((f) => ({ ...f, nama: e.target.value }))}
                                 placeholder="cth. LCD ori"
                                 className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600">Qty</label>
                          <input type="number" min="1" value={partForm.qty} data-testid="srv-part-qty"
                                 onChange={(e) => setPartForm((f) => ({ ...f, qty: e.target.value }))}
                                 className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600">Modal</label>
                          <InputRupiah value={partForm.harga_modal} testid="srv-part-modal"
                                 onChange={(n) => setPartForm((f) => ({ ...f, harga_modal: n }))}
                                 className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                        </div>
                        <div className="flex gap-1 items-end">
                          <div className="flex-1">
                            <label className="text-xs text-slate-600">Jual</label>
                            <InputRupiah value={partForm.harga_jual} testid="srv-part-jual"
                                   onChange={(n) => setPartForm((f) => ({ ...f, harga_jual: n }))}
                                   className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                          </div>
                          <button onClick={tambahPart} disabled={busy || !partForm.nama.trim()}
                                  data-testid="srv-part-add"
                                  className="h-9 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                    <p className="text-[11px] text-slate-400">
                      Sparepart belum memotong stok Inventory (menunggu gerbang cutover stok) — modal dicatat manual.
                    </p>

                    <div className="border-t border-slate-100 pt-2 text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">Total Akhir</span>
                        <span className="font-semibold text-slate-900" data-testid="srv-total">{idr(order.total_akhir)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Sudah Dibayar</span>
                        <span className="text-emerald-700">{idr(order.total_dibayar)}</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Sisa</span>
                        <span className={sisaTagihan > 0.5 ? "text-rose-600 font-semibold" : "text-slate-500"}>{idr(sisaTagihan)}</span></div>
                    </div>
                  </div>

                  {/* Pembayaran */}
                  <div className="border border-slate-200 rounded-xl p-3 space-y-2">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1">
                      <Banknote className="w-3.5 h-3.5" /> Pembayaran
                    </p>
                    {(detail.pembayaran || []).map((p) => (
                      <div key={p.id} className="flex justify-between text-sm border-b border-slate-50 pb-1">
                        <span className="text-slate-600">{p.tanggal} · {p.sumber_akun_nama}</span>
                        <span className="font-medium text-slate-900">{idr(p.nominal)}</span>
                      </div>
                    ))}
                    {(detail.pembayaran || []).length === 0 && (
                      <p className="text-xs text-slate-400">Belum ada pembayaran.</p>
                    )}
                    {bolehUbah && ["COMPLETED", "PICKED_UP"].includes(order.status) && sisaTagihan > 0.5 && (
                      akunList.length > 0 ? (
                        <div className="flex gap-2 items-end pt-1">
                          <div className="flex-1">
                            <label className="text-xs text-slate-600">Nominal (sisa {idr(sisaTagihan)})</label>
                            <InputRupiah value={bayarForm.nominal} testid="srv-bayar-nominal"
                                   onChange={(n) => setBayarForm((f) => ({ ...f, nominal: n }))}
                                   className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                          </div>
                          <div className="flex-1">
                            <label className="text-xs text-slate-600">Masuk ke akun</label>
                            <select value={bayarForm.sumber_akun_id} data-testid="srv-bayar-akun"
                                    onChange={(e) => setBayarForm((f) => ({ ...f, sumber_akun_id: e.target.value }))}
                                    className="mt-1 h-9 w-full px-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                              <option value="">— pilih akun —</option>
                              {akunList.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                          </div>
                          <button onClick={bayar} data-testid="srv-bayar"
                                  disabled={busy || !bayarForm.sumber_akun_id || !(Number(bayarForm.nominal) > 0)}
                                  className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                            Catat
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">
                          Daftar akun kas/bank tidak tersedia untuk akun Anda — minta kasir/finance mencatat pembayaran.
                        </p>
                      )
                    )}
                    {order.status !== "COMPLETED" && order.status !== "PICKED_UP" && (order.total_akhir || 0) > 0 && (
                      <p className="text-[11px] text-slate-400">Pembayaran dicatat setelah status Selesai.</p>
                    )}
                  </div>

                  {/* Garansi & klaim */}
                  <div className="border border-slate-200 rounded-xl p-3 space-y-2">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1">
                      <ShieldCheck className="w-3.5 h-3.5" /> Garansi
                    </p>
                    {order.warranty_expires_at ? (
                      <p className="text-sm text-slate-700">
                        {order.warranty_days} hari — berlaku s.d. <span className="font-medium">{tglPendek(order.warranty_expires_at)}</span>
                        {garansiAktif
                          ? <span className="text-emerald-700 font-medium"> (aktif)</span>
                          : <span className="text-slate-400"> (berakhir)</span>}
                      </p>
                    ) : (
                      <p className="text-xs text-slate-400">Masa garansi dihitung saat servis Selesai.</p>
                    )}
                    {(order.klaim_garansi || []).map((k) => (
                      <p key={k.order_id} className="text-xs text-violet-700">
                        Klaim {tglPendek(k.created_at)}: tiket <span className="font-mono">{k.nomor_tiket}</span> — {k.alasan}
                      </p>
                    ))}
                    {bolehBuat && garansiAktif && (
                      <button onClick={klaimGaransi} disabled={busy} data-testid="srv-klaim"
                              className="h-9 px-3 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        <ShieldCheck className="w-4 h-4" /> Buka Klaim Garansi
                      </button>
                    )}
                  </div>

                  {/* Catatan servis */}
                  <div className="border border-slate-200 rounded-xl p-3 space-y-2">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1">
                      <StickyNote className="w-3.5 h-3.5" /> Catatan Servis
                    </p>
                    {(detail.catatan || []).map((c) => (
                      <div key={c.id} className="text-sm border-b border-slate-50 pb-1.5">
                        <div className="flex items-center gap-1.5 text-[10px]">
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">{c.tipe}</span>
                          {c.is_customer_visible && (
                            <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">terlihat pelanggan</span>
                          )}
                          <span className="text-slate-400">{jamPendek(c.created_at)} · {c.oleh_nama}</span>
                        </div>
                        <p className="text-slate-700 mt-0.5">{c.isi}</p>
                      </div>
                    ))}
                    {(detail.catatan || []).length === 0 && <p className="text-xs text-slate-400">Belum ada catatan.</p>}
                    {bolehUbah && (
                      <div className="space-y-2 pt-1">
                        <div className="flex gap-2">
                          <select value={noteForm.tipe} data-testid="srv-note-tipe"
                                  onChange={(e) => setNoteForm((f) => ({ ...f, tipe: e.target.value }))}
                                  className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                            {NOTE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <label className="flex items-center gap-1.5 text-xs text-slate-600">
                            <input type="checkbox" checked={noteForm.is_customer_visible} data-testid="srv-note-visible"
                                   onChange={(e) => setNoteForm((f) => ({ ...f, is_customer_visible: e.target.checked }))}
                                   className="accent-emerald-600" />
                            Boleh dilihat pelanggan
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <input value={noteForm.isi} data-testid="srv-note-isi"
                                 placeholder="Tulis catatan diagnosa / perbaikan..."
                                 onChange={(e) => setNoteForm((f) => ({ ...f, isi: e.target.value }))}
                                 className="h-9 flex-1 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                          <button onClick={tambahCatatan} disabled={busy || noteForm.isi.trim().length < 2}
                                  data-testid="srv-note-add"
                                  className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm disabled:opacity-50">
                            Simpan
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Riwayat status */}
                  <div className="border border-slate-200 rounded-xl p-3">
                    <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1 mb-2">
                      <History className="w-3.5 h-3.5" /> Riwayat Status
                    </p>
                    <div className="space-y-2">
                      {(detail.riwayat || []).map((h) => (
                        <div key={h.id} className="flex gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${STATUS_META[h.status_baru]?.bar || "bg-slate-300"}`} />
                          <div>
                            <p className="text-slate-800">
                              {h.status_lama !== h.status_baru
                                ? `${STATUS_META[h.status_lama]?.label || h.status_lama} → ${STATUS_META[h.status_baru]?.label || h.status_baru}`
                                : (STATUS_META[h.status_baru]?.label || h.status_baru)}
                              {h.catatan && <span className="text-slate-500"> — {h.catatan}</span>}
                            </p>
                            <p className="text-[11px] text-slate-400">{jamPendek(h.created_at)} · {h.oleh_nama}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ============ Modal intake ============ */}
      {intake && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
             onClick={intakeGuard.requestClose}>
          <div ref={intakeGuard.contentRef} className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <p className="font-display font-bold text-base text-slate-900">Terima Unit Servis</p>
              <button onClick={intakeGuard.requestClose} data-testid="srv-intake-close"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Nama Pelanggan <span className="text-rose-500">*</span></label>
                  <input value={intake.pelanggan_nama} data-testid="srv-f-nama"
                         onChange={(e) => setIntake((f) => ({ ...f, pelanggan_nama: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">No. HP / WhatsApp</label>
                  <input value={intake.no_hp} inputMode="tel" placeholder="08xxxxxxxxxx" data-testid="srv-f-hp"
                         onChange={(e) => setIntake((f) => ({ ...f, no_hp: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Model Perangkat <span className="text-rose-500">*</span></label>
                  <input value={intake.device_model} placeholder="cth. iPhone 13" data-testid="srv-f-model"
                         onChange={(e) => setIntake((f) => ({ ...f, device_model: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Storage</label>
                  <input value={intake.storage} placeholder="128GB" data-testid="srv-f-storage"
                         onChange={(e) => setIntake((f) => ({ ...f, storage: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Warna</label>
                  <input value={intake.warna} data-testid="srv-f-warna"
                         onChange={(e) => setIntake((f) => ({ ...f, warna: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">IMEI</label>
                  <input value={intake.imei} inputMode="numeric" data-testid="srv-f-imei"
                         onChange={(e) => setIntake((f) => ({ ...f, imei: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Cabang</label>
                  <input value={intake.cabang} data-testid="srv-f-cabang"
                         onChange={(e) => setIntake((f) => ({ ...f, cabang: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Keluhan <span className="text-rose-500">*</span></label>
                <textarea value={intake.keluhan} rows={2} data-testid="srv-f-keluhan"
                          placeholder="cth. LCD pecah, touchscreen sebagian mati"
                          onChange={(e) => setIntake((f) => ({ ...f, keluhan: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Teknisi</label>
                  <select value={intake.teknisi_id} data-testid="srv-f-teknisi"
                          onChange={(e) => setIntake((f) => ({ ...f, teknisi_id: e.target.value }))}
                          className="mt-1 h-10 w-full px-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                    <option value="">— pilih nanti —</option>
                    {teknisiList.map((t) => <option key={t.id} value={t.id}>{t.nama}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Prioritas</label>
                  <select value={intake.prioritas} data-testid="srv-f-prioritas"
                          onChange={(e) => setIntake((f) => ({ ...f, prioritas: e.target.value }))}
                          className="mt-1 h-10 w-full px-2 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500">
                    <option>Normal</option><option>Urgent</option><option>Low</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Estimasi Biaya (Rp)</label>
                  <InputRupiah value={intake.estimasi_biaya} testid="srv-f-estimasi-awal"
                         onChange={(n) => setIntake((f) => ({ ...f, estimasi_biaya: n }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Biaya Jasa (Rp)</label>
                  <InputRupiah value={intake.service_fee} testid="srv-f-jasa-awal"
                         onChange={(n) => setIntake((f) => ({ ...f, service_fee: n }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Garansi (hari)</label>
                  <input type="number" value={intake.warranty_days} data-testid="srv-f-garansi-awal"
                         onChange={(e) => setIntake((f) => ({ ...f, warranty_days: e.target.value }))}
                         className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Catatan Awal Teknisi</label>
                <input value={intake.catatan_awal} data-testid="srv-f-catatan-awal"
                       placeholder="opsional — kondisi fisik, kelengkapan, dsb."
                       onChange={(e) => setIntake((f) => ({ ...f, catatan_awal: e.target.value }))}
                       className="mt-1 h-10 w-full px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                  <Camera className="w-3.5 h-3.5" /> Foto Serah Terima (maks 4, otomatis dikompres)
                </label>
                <input type="file" accept="image/*" multiple data-testid="srv-f-foto"
                       onChange={(e) => { tambahFoto(e.target.files); e.target.value = ""; }}
                       disabled={(intake.photos || []).length >= 4}
                       className="mt-1 text-sm text-slate-600 file:mr-3 file:h-9 file:px-3 file:rounded-lg file:border-0 file:bg-slate-100 file:text-slate-700 file:text-sm hover:file:bg-slate-200" />
                {(intake.photos || []).length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {intake.photos.map((p, i) => (
                      <div key={i} className="relative">
                        <img src={p} alt={`Foto ${i + 1}`} className="w-16 h-16 object-cover rounded-lg border border-slate-200" />
                        <button onClick={() => setIntake((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                                data-testid={`srv-f-foto-del-${i}`}
                                className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={intakeGuard.requestClose}
                      className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">
                Batal
              </button>
              <button onClick={simpanIntake} data-testid="srv-save"
                      disabled={busy || !intake.pelanggan_nama.trim() || !intake.device_model.trim() || !intake.keluhan.trim()}
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {busy ? "Menyimpan..." : "Buat Tiket"}
              </button>
            </div>
            {intakeGuard.confirming && (
              <UnsavedGuardBar onStay={intakeGuard.cancelClose} onDiscard={intakeGuard.confirmDiscard} testid="srv-intake-guard-bar" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
